import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {GeoCoord} from "./geo-coord";
import {
  DispatchDispatchRequestAssoc,
  DispatchDispatchRequestAssocArrayValidator,
  DispatchDispatchRequestAssocEntity
} from "./dispatch-dispatch-request-assoc";
import {TransportCategory} from "./transport-category";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer, getTransportCategoryTransformer} from "./transfomers/transformers";
import {adjustToTimeZone} from "../date/date";
import {DatabaseCode, toDatabaseCode} from "./database-code";
import {joinSafely} from "../util/string-util";
import {Dispatch} from "./dispatch";
import {ObjectIntegrationStatus, ObjectIntegrationStatusCodec} from "./integration-history";

/** Validator for a dispatch request type. */
export const DispatchRequestValidator = t.intersection([
  t.type({
    id: t.string,
    regionId: t.string,
    status: t.string,
    operatorId: t.string,
    truckingCompanyId: t.string,
    quantityDecimal: t.number,
    quantityUnitOfMeasure: t.string,
    dispatchAcknowledged: t.boolean,
    scheduledDttm: t.number,
    authorized: t.boolean,
    category: t.string
  }),
  t.partial({
    createdById: t.string,
    externalId: t.string,
    externalSystem: t.string,
    workOrderNumber: t.string,
    pickupSiteId: t.string,
    pickupWellId: t.string,
    pickupContainerId: t.string,
    deliverySiteId: t.string,
    deliveryWellId: t.string,
    deliveryContainerId: t.string,
    freightTypeId: t.string,
    containerAlertId: t.string,
    authorizeId: t.string,
    authorizeDttm: t.number,
    statusChangeDttm: t.number,
    dispatchAcknowledgedDttm: t.number,
    scheduledEndDttm: t.number,
    latCoord: t.number,
    longCoord: t.number,
    pickupSiteName: t.string,
    pickupWellName: t.string,
    pickupContainerName: t.string,
    pickupSiteCompanyId: t.string,
    pickupSiteCompanyName: t.string,
    pickupSiteCompanyType: t.string,
    deliverySiteName: t.string,
    deliveryWellName: t.string,
    deliveryContainerName: t.string,
    deliverySiteCompanyId: t.string,
    deliverySiteCompanyName: t.string,
    deliverySiteCompanyType: t.string,
    truckingCompanyName: t.string,
    operatorName: t.string,
    freightTypeName: t.string,
    createdByLastName: t.string,
    createdByFirstName: t.string,
    authorizeLastName: t.string,
    authorizeFirstName: t.string,
    authorizeCompanyId: t.string,
    resourceId: t.number,
    dispatchRequestAssocList: DispatchDispatchRequestAssocArrayValidator,
    fakeStatus: t.string,
    realStatus: t.string,
    operatorModifiedAckDttm: t.number,
    locationDttm: t.number,
    truckCompanyComment: t.string,
    comment: t.string,
    operatorModifiedDttm: t.number,
    serviceTypeId: t.string,
    serviceTypeName: t.string,
    acceptTimeLimit: t.number,
    reassignedById: t.string,
    reassignedByFirstName: t.string,
    reassignedByLastName: t.string,
    reassignedDttm: t.number,
    reassignedFromDispatchRequestId: t.string,
    reassignedFromResourceId: t.string,
    reassignedFromTruckCompanyName: t.string,
    reassignedToDispatchRequestId: t.string,
    reassignedToResourceId: t.string,
    reassignedToTruckCompanyName: t.string,
    acceptedDttm: t.number,
    acceptedById: t.string,
    acceptedByFirstName: t.string,
    acceptedByLastName: t.string,
    rejectedDttm: t.number,
    rejectedById: t.string,
    rejectedByFirstName: t.string,
    rejectedByLastName: t.string,
    canceledDttm: t.number,
    canceledById: t.string,
    canceledByFirstName: t.string,
    canceledByLastName: t.string,
    unresolvedDttm: t.number,
    unresolvedById: t.string,
    unresolvedByFirstName: t.string,
    unresolvedByLastName: t.string,
    reassignmentImminent: t.boolean,
    authentication: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,

    objectIntegrationStatus: ObjectIntegrationStatusCodec,
  })
]);

export const DispatchRequestArrayValidator = t.array(DispatchRequestValidator);

export type DispatchRequest = t.TypeOf<typeof DispatchRequestValidator>;
export type DispatchRequestArray = t.TypeOf<typeof DispatchRequestArrayValidator>;

export interface DispatchRequestStatus extends DatabaseCode {
}

const pending: DispatchRequestStatus = {
  code: "PENDING",
  displayValue: {
    id: "dispatchrequest.status.pending",
    defaultMessage: "Pending"
  }
};

const pendingCancel: DispatchRequestStatus = {
  code: "PENDING_CANCEL",
  displayValue: {
    id: "dispatchrequest.status.pendingcancel",
    defaultMessage: "Canceled"
  }
};

const accepted: DispatchRequestStatus = {
  code: "ACCEPTED",
  displayValue: {
    id: "dispatchrequest.status.accepted",
    defaultMessage: "Accepted"
  }
};

const rejected: DispatchRequestStatus = {
  code: "REJECTED",
  displayValue: {
    id: "dispatchrequest.status.rejected",
    defaultMessage: "Rejected"
  }
};

const reassigned: DispatchRequestStatus = {
  code: "REASSIGNED",
  displayValue: {
    id: "dispatchrequest.status.reassigned",
    defaultMessage: "Reassigned"
  }
};

const canceled: DispatchRequestStatus = {
  code: "CANCELED",
  displayValue: {
    id: "dispatchrequest.status.canceled",
    defaultMessage: "Canceled"
  }
};

const unresolved: DispatchRequestStatus = {
  code: "UNRESOLVED",
  displayValue: {
    id: "dispatchrequest.status.unresolved",
    defaultMessage: "Unresolved"
  }
};

const started: DispatchRequestStatus = {
  code: "STARTED",
  displayValue: {
    id: "dispatchrequest.status.started",
    defaultMessage: "Started"
  }
};

const pickedUp: DispatchRequestStatus = {
  code: "PICKED_UP",
  displayValue: {
    id: "dispatchrequest.status.pickedup",
    defaultMessage: "Picked-Up"
  }
};

const completed: DispatchRequestStatus = {
  code: "COMPLETED",
  displayValue: {
    id: "dispatchrequest.status.completed",
    defaultMessage: "Completed"
  }
};

export const dispatchRequestStatus = {
  pending,
  pendingCancel,
  accepted,
  rejected,
  reassigned,
  canceled,
  completed,
  pickedUp,
  started,
  unresolved
};

export const dispatchRequestStatuses = Object.values(dispatchRequestStatus);

export const toDispatchRequestStatus = (code: string): DispatchRequestStatus => toDatabaseCode(code, dispatchRequestStatuses);

@Exclude()
export class DispatchRequestEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  @Transform(getTransportCategoryTransformer())
  category!: TransportCategory;

  @Expose()
  regionId!: string;

  @Expose({name: "status"})
  @Transform(getDatabaseCodeTransformer(toDispatchRequestStatus))
  dbStatus!: DispatchRequestStatus;

  @Expose({name: "fakeStatus"})
  @Transform(getDatabaseCodeTransformer(toDispatchRequestStatus))
  computedStatus?: DispatchRequestStatus;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toDispatchRequestStatus))
  realStatus?: DispatchRequestStatus;

  @Expose({name: "quantityDecimal"})
  quantity!: number;

  @Expose({name: "quantityUnitOfMeasure"})
  quantityUom!: string;

  @Expose()
  truckingCompanyId!: string;

  @Expose()
  operatorId!: string;

  @Expose()
  createdById?: string;

  @Expose({name: "dispatchAcknowledged"})
  isDispatchAcknowledged = false;

  @Expose()
  scheduledDttm!: number;

  @Expose({name: "authorized"})
  isAuthorized = true;

  @Expose()
  pickupSiteId?: string;

  @Expose()
  pickupWellId?: string;

  @Expose()
  pickupContainerId?: string;

  @Expose()
  deliverySiteId?: string;

  @Expose()
  deliveryWellId?: string;

  @Expose()
  deliveryContainerId?: string;

  @Expose()
  freightTypeId?: string;

  @Expose()
  containerAlertId?: string;

  @Expose()
  authorizeId?: string;

  @Expose()
  authorizeDttm?: number;

  @Expose()
  statusChangeDttm?: number;

  @Expose()
  dispatchAcknowledgedDttm?: number;

  @Expose()
  scheduledEndDttm?: number;

  @Expose()
  latCoord?: number;

  @Expose()
  longCoord?: number;

  @Expose()
  pickupSiteName?: string;

  @Expose()
  pickupWellName?: string;

  @Expose()
  pickupContainerName?: string;

  @Expose()
  pickupSiteCompanyId?: string;

  @Expose()
  pickupSiteCompanyName?: string;

  @Expose()
  pickupSiteCompanyType?: string;

  @Expose()
  deliverySiteName?: string;

  @Expose()
  deliveryWellName?: string;

  @Expose()
  deliveryContainerName?: string;

  @Expose()
  deliverySiteCompanyId?: string;

  @Expose()
  deliverySiteCompanyName?: string;

  @Expose()
  deliverySiteCompanyType?: string;

  @Expose()
  truckingCompanyName?: string;

  @Expose()
  operatorName?: string;

  @Expose()
  freightTypeName?: string;

  @Expose()
  createdByLastName?: string;

  @Expose()
  createdByFirstName?: string;

  @Expose()
  authorizeLastName?: string;

  @Expose()
  authorizeFirstName?: string;

  @Expose()
  authorizeCompanyId?: string;

  @Expose()
  externalId?: string;

  @Expose()
  externalSystem?: string;

  @Expose()
  workOrderNumber?: string;

  @Expose()
  resourceId?: number;

  @Expose()
  dispatchRequestAssocList?: DispatchDispatchRequestAssoc[];

  @Expose()
  operatorModifiedAckDttm?: number;

  @Expose()
  locationDttm?: number;

  @Expose()
  truckCompanyComment?: string;

  @Expose({name: "comment"})
  operatorComment?: string;

  @Expose()
  operatorModifiedDttm?: number;

  @Expose()
  serviceTypeId?: string;

  @Expose()
  serviceTypeName?: string;

  @Expose()
  acceptTimeLimit?: number;

  @Expose()
  reassignedById?: string;

  @Expose()
  reassignedByFirstName?: string;

  @Expose()
  reassignedByLastName?: string;

  @Expose()
  reassignedDttm?: number;

  @Expose()
  reassignedFromDispatchRequestId?: string;

  @Expose()
  reassignedFromResourceId?: string;

  @Expose()
  reassignedFromTruckCompanyName?: string;

  @Expose()
  reassignedToDispatchRequestId?: string;

  @Expose()
  reassignedToResourceId?: string;

  @Expose()
  reassignedToTruckCompanyName?: string;

  @Expose()
  acceptedDttm?: number;

  @Expose()
  acceptedById?: string;

  @Expose()
  acceptedByFirstName?: string;

  @Expose()
  acceptedByLastName?: string;

  @Expose()
  rejectedDttm?: number;

  @Expose()
  rejectedById?: string;

  @Expose()
  rejectedByFirstName?: string;

  @Expose()
  rejectedByLastName?: string;

  @Expose()
  canceledDttm?: number;

  @Expose()
  canceledById?: string;

  @Expose()
  canceledByFirstName?: string;

  @Expose()
  canceledByLastName?: string;

  @Expose()
  unresolvedDttm?: number;

  @Expose()
  unresolvedById?: string;

  @Expose()
  unresolvedByFirstName?: string;

  @Expose()
  unresolvedByLastName?: string;

  @Expose()
  reassignmentImminent = false

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  objectIntegrationStatus?: ObjectIntegrationStatus;

  readonly entityType = "DISPATCHREQUEST";

  get displayName(): Readonly<string> {
    return this.resourceId?.toString() ?? this.id;
  }

  /**
   * Constructor for DispatchRequestEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(dispatchRequest: DispatchRequest): DispatchRequestEntity {
    return plainToInstance(
      DispatchRequestEntity,
      dispatchRequest,
      {
        excludeExtraneousValues: true
      }
    );
  }

  /**
   * Deserializes the DispatchRequestEntity to a DispatchRequest type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<DispatchRequest> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSON(DispatchRequestValidator, object);
  }

  asTypeValue(): DispatchRequest {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(DispatchRequestValidator, object);
  }

  get createdBy(): string | undefined {
    return joinSafely(" ", this.createdByFirstName, this.createdByLastName)
  }

  get authorizedBy(): string | undefined {
    return joinSafely(" ", this.authorizeFirstName, this.authorizeLastName)
  }

  get status(): DispatchRequestStatus {
    return this.realStatus ?? this.computedStatus ?? this.dbStatus;
  }

  get quantityDisplay(): string {
    if (this.quantity && this.quantityUom) {
      return this.quantity + " " + this.quantityUom;
    } else if (this.quantity) {
      return this.quantity.toString();
    }
    return "-";
  }

  /**
   * Return display value for the pickup location.
   */
  get pickupLocation(): string {
    const pickupContainerName = this.pickupContainerName;
    const pickupSiteName = this.pickupSiteName;

    if (pickupSiteName && pickupContainerName) {
      return pickupSiteName + " / " + pickupContainerName;
    } else if (pickupSiteName) {
      return pickupSiteName;
    } else if (pickupContainerName) {
      return pickupContainerName;
    } else {
      return "Not Specified";
    }
  }

  /**
   * Return display value for the delivery location.
   */
  get deliveryLocation(): string {
    const deliveryContainerName = this.deliveryContainerName;
    const deliverySiteName = this.deliverySiteName;

    if (deliverySiteName && deliveryContainerName) {
      return deliverySiteName + " / " + deliveryContainerName;
    } else if (deliverySiteName) {
      return deliverySiteName;
    } else if (deliveryContainerName) {
      return deliveryContainerName;
    } else {
      return "Not Specified";
    }
  }

  get dispatches(): DispatchDispatchRequestAssocEntity[] {
    if (this.dispatchRequestAssocList) {
      return this.dispatchRequestAssocList.map((assoc) =>
        DispatchDispatchRequestAssocEntity.from(assoc)
      );
    } else {
      return [];
    }
  }

  get driverLocation(): GeoCoord | undefined {
    if (this.latCoord && this.longCoord) {
      return {
        latCoord: this.latCoord,
        longCoord: this.longCoord
      };
    }

    return undefined;
  }

  get isLate(): boolean {
    let status = false;
    if (
      this.status.code === dispatchRequestStatus.pending.code ||
      this.status.code === dispatchRequestStatus.accepted.code ||
      this.status.code === dispatchRequestStatus.pickedUp.code
    ) {
      status = this.scheduledEndDttm
        ? this.scheduledEndDttm < adjustToTimeZone(new Date()).getTime()
        : false;
    }

    return status;
  }

  get reassignedFromId(): string | undefined {
    return this.reassignedFromDispatchRequestId;
  }

  set reassignedFromId(id: string | undefined) {
    this.reassignedFromDispatchRequestId = id;
  }

  get reassignedToId(): string | undefined {
    return this.reassignedToDispatchRequestId;
  }

  set reassignedToId(id: string | undefined) {
    this.reassignedToDispatchRequestId = id;
  }

  get isModified(): boolean {
    return typeof this.operatorModifiedDttm !== "undefined" && (!this.operatorModifiedAckDttm || this.operatorModifiedAckDttm < this.operatorModifiedDttm);
  }
}

export type DispatchRequestSearchCriteria = {
  fromDttm?: number;
  toDttm?: number;
  regionId?: string;
  statuses?: string[];
  categories?: string[];
  authorized?: boolean;
};

export type DispatchRequestReassign = {
  dispatchRequestId: string
  companyId: string
  comments: string
};

export type DispatchRequestDispatch = {
  dispatchRequestId: string
  dispatch?: Partial<Dispatch>
  copyDispatchRequestComments?: boolean
};

export type DispatchRequestReject = {
  dispatchRequestId: string
  comments: string
};

